/**
 * Where a child is, and what they may play next.
 *
 * The chapter's heading, its progress and its offers come from the chapter
 * screen (`05-chapter`); the lessons themselves are drawn as the winding path
 * of `03-path` ([D-140](../../../docs/DECISIONS.md#d-140)), with the child
 * standing on the next step and their pet beside them.
 *
 * Which node is open is decided by the server and shown here, never worked out
 * again on this side — `drawPath` in `packages/core` is the only place that rule
 * lives.
 */

import '@capytalk/tokens/blocks/chapter.css';

import { useCallback, useEffect, useState } from 'react';
import {
  type Client,
  type Path as ChapterPath,
  type Trouble,
  today,
  troubleFrom,
} from './api.js';
import Books from './Books.js';
import Placement from './Placement.js';
import Sneakers from './Sneakers.js';
import WereYouAway from './WereYouAway.js';
import Winding, { type Companion, type Standing } from './Winding.js';

const minutes = (seconds: number): string =>
  `about ${String(Math.max(1, Math.round(seconds / 60)))} min`;

/** How many passes there are, and therefore how much of the ring one fills. */
const PASSES = 4;

/**
 * The chapter ring: ○ ◔ ◑ ◕ ●, drawn rather than typed
 * ([D-109](../../../docs/DECISIONS.md#d-109)).
 *
 * Those characters were the first implementation and they were wrong. Most
 * system font stacks have `○` and `●` and not the three in between, so a
 * half-filled ring fell back to an illegible speck — which a rendered mockup
 * showed at once and no unit test would ever have caught.
 *
 * So it is an arc: one circle, plus a wedge sweeping clockwise from twelve
 * o'clock. It scales with the text around it, inherits the heading's colour,
 * and looks the same on every device.
 */
function Ring({ passes }: { readonly passes: number }) {
  const done = Math.min(Math.max(passes, 0), PASSES);
  const label =
    done === 0
      ? 'Not finished yet'
      : done >= PASSES
        ? 'Mastered'
        : `Finished ${String(done)} of ${String(PASSES)} ways`;

  // Clockwise from the top. The half-circle case needs `large-arc` off and the
  // three-quarter case needs it on, which is what the comparison decides.
  const turn = (done / PASSES) * 2 * Math.PI;
  const wedge =
    done === 0 ? null : done >= PASSES ? (
      // A full circle cannot be one arc — its start and end points are the
      // same, so the renderer has nothing to sweep. A plain disc instead.
      <circle cx="8" cy="8" r="6" fill="currentColor" />
    ) : (
      <path
        d={`M 8 8 L 8 2 A 6 6 0 ${turn > Math.PI ? 1 : 0} 1 ${String(
          (8 + 6 * Math.sin(turn)).toFixed(3),
        )} ${String((8 - 6 * Math.cos(turn)).toFixed(3))} Z`}
        fill="currentColor"
      />
    );

  return (
    <svg
      viewBox="0 0 16 16"
      width="0.8em"
      height="0.8em"
      role="img"
      // Labelled rather than hidden: the ring *is* the information, and a
      // screen reader that says nothing at all leaves a child without it.
      aria-label={label}
      style={{ opacity: 0.55, verticalAlign: 'baseline' }}
    >
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      {wedge}
    </svg>
  );
}

export default function Path({
  client,
  onPlay,
  onPractise,
  onBonus,
  opening = null,
}: {
  readonly client: Client;
  /**
   * The chapter to open on, when the app knows better than "wherever the
   * server puts me": the next one, after a lesson that finished a chapter
   * ([D-183](../../../docs/DECISIONS.md#d-183)). Null for the usual.
   */
  readonly opening?: string | null;
  /**
   * Start a lesson, optionally as part of a chapter replay
   * ([D-109](../../../docs/DECISIONS.md#d-109)).
   *
   * The level rides along rather than being remembered by the server, because
   * "which pass am I on" is a thing a child changes their mind about between
   * one lesson and the next — and because the server clamps it anyway.
   */
  readonly onPlay: (lessonId: string, replayLevel?: number) => void;
  /** Open the mistakes pool as a session of its own. */
  readonly onPractise: () => void;
  /**
   * Pick up a bonus round that was offered and never played
   * ([D-110](../../../docs/DECISIONS.md#d-110)).
   *
   * The offer lives in a row keyed by the day, so an app closed between
   * "yes please" and the first question leaves one stranded. Without this it
   * would be unreachable until tomorrow — earned, recorded, and invisible.
   */
  readonly onBonus?: ((day: string) => void) | undefined;
}) {
  const [path, setPath] = useState<ChapterPath | null>(null);
  const [trouble, setTrouble] = useState<Trouble | null>(null);
  /**
   * Bumped when the placement test finishes, to fetch the path again.
   *
   * The placement decides which chapter this screen opens on, so the answer it
   * just wrote is not in the copy already loaded.
   */
  const [placed, setPlaced] = useState(0);
  /**
   * Reports of theirs that have been looked at and not yet mentioned.
   *
   * The half of *"something's wrong here"* that makes a child report the
   * **second** bug ([D-073](../../../docs/DECISIONS.md#d-073)). Shown on the
   * path rather than in a lesson, because a report is answered days later and
   * this is the screen they come back to.
   */
  const [fixes, setFixes] = useState<readonly { id: string }[]>([]);
  /** How many things are waiting to be practised, and when it is worth offering. */
  const [pool, setPool] = useState<{ waiting: number; offerAt: number } | null>(null);
  /**
   * The chapter being looked at, or null for "wherever the server puts me".
   *
   * Without this a finished chapter is unreachable: the path always opens on
   * the chapter a child is *on*, so the one screen that offers a replay would
   * be the one screen they can never get back to
   * ([D-109](../../../docs/DECISIONS.md#d-109)). Found by driving it in a
   * browser — every test passed with the button nobody could reach.
   *
   * Looking ahead is safe and deliberate: the lessons of a chapter they have
   * not reached come back locked from the server, and seeing what is coming
   * without being able to jump to it is already how the node list works.
   */
  const [viewing, setViewing] = useState<string | null>(opening);
  /** A round offered today and not yet played. Null on almost every launch. */
  const [waiting, setWaiting] = useState<{ badge: string; nameL1: string } | null>(
    null,
  );

  /** Bumped by "Try again", which is all it takes to ask again. */
  const [attempt, setAttempt] = useState(0);

  /** The child and their companion, for the step they stand on. Both optional. */
  const [standing, setStanding] = useState<Standing | null>(null);
  const [companion, setCompanion] = useState<Companion | null>(null);

  useEffect(() => {
    let live = true;
    // Decoration: a failure draws the path without them, never an error.
    Promise.resolve()
      .then(() => client.shop.loadout.query())
      .then((look) => {
        if (live && look.worn !== undefined) {
          setStanding({
            species: look.species,
            background: look.background,
            worn: look.worn,
          });
        }
      })
      .catch(() => undefined);
    Promise.resolve()
      .then(() => client.pets.shelf.query())
      .then((shelf) => {
        if (!live) return;
        const pet = shelf.pets.find((one) => one.active);
        const egg = shelf.eggs[0];
        // The pet they chose to bring, or else the egg they are hatching.
        if (pet !== undefined) setCompanion({ emoji: pet.emoji, name: pet.nameL1 });
        else if (egg !== undefined) setCompanion({ emoji: '🥚', name: 'Your egg' });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [client]);

  useEffect(() => {
    let live = true;
    setTrouble(null);
    client.path.chapter
      .query(viewing === null ? undefined : { chapterId: viewing })
      .then((next) => {
        if (live) setPath(next);
      })
      .catch((error: unknown) => {
        if (live) setTrouble(troubleFrom(error));
      });
    // Asked for separately rather than folded into the path, because the pool is
    // derived from a month of attempts and the path is drawn on every return
    // from a lesson. A failure here shows no offer, which is the right way to be
    // wrong: practice is optional.
    // Through `Promise.resolve` because the call can *throw* rather than reject
    // on a server older than this app, which has no `practice` router at all
    // (D-066) — and a synchronous throw here would take the whole path screen
    // down for a feature that is optional.
    Promise.resolve()
      .then(() => client.practice.waiting.query())
      .then((next) => {
        if (live) setPool(next);
      })
      .catch(() => undefined);

    // Same shape and the same reason: a server one deploy behind has no
    // `fixes` route at all and throws *synchronously*
    // ([D-066](../../../docs/DECISIONS.md#d-066)). Nothing to say is the
    // correct way for this to fail.
    Promise.resolve()
      .then(() => client.learner.fixes.query())
      .then((next) => {
        if (live && Array.isArray(next)) setFixes(next);
      })
      .catch(() => undefined);

    // And again, for the same reason. A bonus round left unplayed is the rarest
    // thing on this screen; silence is the right way for it to fail.
    Promise.resolve()
      .then(() => client.bonus.waiting.query({ day: today() }))
      .then((next) => {
        if (live && next !== null) setWaiting(next);
      })
      .catch(() => undefined);

    return () => {
      live = false;
    };
    // A lesson takes over the whole screen, so this unmounts while one is being
    // played and mounts again on the way back. That is the refresh: coming out
    // of a lesson always redraws the path, because finishing one may have
    // opened the next node.
  }, [client, placed, viewing, attempt]);

  /**
   * The pass this child is playing right now, or null for the ordinary one.
   *
   * Local to this screen and forgotten on the way out. A replay is a mood, not
   * a setting: a child who taps "play it again" and then leaves should come
   * back to their ordinary lessons rather than to a harder game they have
   * forgotten choosing.
   */
  const [replaying, setReplaying] = useState<{ level: number; name: string } | null>(
    null,
  );

  /**
   * Turn to another chapter, and stop replaying.
   *
   * The pass belongs to the chapter it was chosen on. Carrying it across would
   * leave the banner announcing "Speaking focus" over a chapter the child has
   * never finished — where the server clamps it to an ordinary lesson anyway,
   * so the screen would be the only thing still saying it.
   */
  const turnTo = useCallback((chapterId: string | null) => {
    if (chapterId === null) return;
    setReplaying(null);
    setViewing(chapterId);
  }, []);

  /** The shelf of books, over the path, when the header is tapped. */
  const [shelf, setShelf] = useState(false);

  const play = useCallback(
    (lessonId: string) => {
      onPlay(lessonId, replaying?.level);
    },
    [onPlay, replaying],
  );

  /**
   * Say thank you, once, and spend the message.
   *
   * Marked seen when the child dismisses it rather than when it renders: a
   * card that appears while they are tapping something else has not been read,
   * and "told exactly once" should mean told.
   *
   * Above every early return below, and it has to stay there. The screen has
   * four of them — the placement offer, the error, the loading state, the path
   * itself — so a hook declared between any two of them runs on some renders
   * and not others, and React counts hooks. It cost a whole blank screen to
   * learn ([D-099](../../../docs/DECISIONS.md#d-099)).
   */
  const thanked = useCallback(() => {
    const reportIds = fixes.map((fix) => fix.id);
    setFixes([]);
    void Promise.resolve()
      .then(() => client.learner.sawFixes.mutate({ reportIds }))
      .catch(() => undefined);
  }, [client, fixes]);

  /*
    Never placed, so offer the test — here rather than in front of the whole app,
    because the tab bar staying visible is what makes it an offer rather than a
    gate. `placedAt` is only null until they answer *or skip*: skipping writes
    the first chapter, so a child who said no is not asked again tomorrow
    ([Adaptive engine](../../../docs/ADAPTIVE-ENGINE.md#placement-test)).

    Read defensively: a server older than this app sends no `placedAt` at all,
    and `undefined` must mean "do not offer" rather than "offer forever"
    ([D-066](../../../docs/DECISIONS.md#d-066)).
  */
  if (shelf) {
    return (
      <Books
        client={client}
        onOpen={(chapterId) => {
          setShelf(false);
          turnTo(chapterId);
        }}
        onBack={() => {
          setShelf(false);
        }}
      />
    );
  }

  if (path !== null && 'placedAt' in path && path.placedAt === null) {
    return (
      <Placement
        client={client}
        onDone={() => {
          setPath(null);
          setPlaced((n) => n + 1);
        }}
      />
    );
  }

  if (trouble !== null) {
    return (
      <div className="grow pad center">
        <p className="sub" role="alert">
          {trouble.kind === 'offline'
            ? 'No connection. Is the wi-fi on?'
            : 'The map would not load.'}
        </p>
        {/* Never a dead end. A request that failed because a session was
            renewing a moment later (D-131), or because the wi-fi dropped, is
            one tap from working. */}
        <button
          className="btn ghost"
          type="button"
          style={{ marginTop: 12 }}
          onClick={() => {
            setAttempt((n) => n + 1);
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (path === null) {
    return (
      <div className="grow pad center">
        <p className="sub">Getting your map…</p>
      </div>
    );
  }

  const finished = path.lessons.filter((l) => l.state === 'done').length;

  // Read defensively. A server one deploy behind this app answers with the path
  // this route used to return, and reaching into a `replay` that is not there
  // white-screens the *first* screen of the app for everybody until the deploy
  // catches up ([D-066](../../../docs/DECISIONS.md#d-066)).
  const passes = path.passes ?? 0;
  const replay = path.replay ?? { kind: 'finish-first' as const };

  // From the server, which is holding the outline. Deriving them from the id
  // would be string arithmetic over a course structure — `en.b1.c5` is followed
  // by `en.b2.c1`, not `en.b1.c6`. Null-safe for a server one deploy behind
  // ([D-066](../../../docs/DECISIONS.md#d-066)), where the arrows simply do not
  // appear.
  const before = path.previousChapterId ?? null;
  const after = path.nextChapterId ?? null;

  // Every book finished: the path carries on as the Long Path (mockup 40,
  // D-150). Null from a server one deploy behind, which simply has none.
  const longPath = path.longPath ?? null;

  return (
    <div className="grow pad">
      {/*
        Turning the pages. A chapter a child has finished is behind them, and
        the replay lives on its screen — so without a way back the offer is
        unreachable ([D-109](../../../docs/DECISIONS.md#d-109)).
      */}
      <div className="between">
        <button
          type="button"
          className="btn ghost"
          style={{ width: 'auto', minWidth: 48, minHeight: 48, padding: '0 14px' }}
          disabled={before === null}
          aria-label="The chapter before this one"
          onClick={() => {
            turnTo(before);
          }}
        >
          ‹
        </button>
        {/*
          Where they are, and the way to the other books (mockup 04). A button
          because it opens the shelf; it looks like the kicker it replaced.
        */}
        <button
          type="button"
          className="kicker"
          aria-label="Your books"
          style={{
            background: 'none',
            border: 0,
            font: 'inherit',
            cursor: 'pointer',
            minHeight: 48,
            padding: '0 8px',
          }}
          onClick={() => {
            setShelf(true);
          }}
        >
          {/* A server one deploy behind sends no book, and the book is left off. */}
          {typeof path.book === 'number' ? `📚 Book ${String(path.book)} · ` : ''}
          Chapter {path.ordinal} of {path.chapterCount}
        </button>
        <button
          type="button"
          className="btn ghost"
          style={{ width: 'auto', minWidth: 48, minHeight: 48, padding: '0 14px' }}
          disabled={after === null}
          aria-label="The chapter after this one"
          onClick={() => {
            turnTo(after);
          }}
        >
          ›
        </button>
      </div>
      {longPath === null ? (
        <h2>
          {path.titleL1}{' '}
          {/*
            The ring, from the server. Never worked out again here: two clients
            drawing two alphabets for the same progress is exactly the drift the
            server sending a character rather than a number avoids
            ([D-109](../../../docs/DECISIONS.md#d-109)).
          */}
          <Ring passes={passes} />
        </h2>
      ) : (
        <>
          <h2>The Long Path</h2>
          {/*
            Honest about what this is, because a child can tell
            ([Generation](../../../docs/GENERATION.md#what-the-child-sees)).
            Not said aloud on arrival: this is the screen a finished child
            opens every day, and the line is for reading once.
          */}
          <div style={{ marginTop: 8 }}>
            <Sneakers
              text="You learned everything in my books! Now I make you new practice."
              audioRef={null}
              size={48}
              speak={false}
            />
          </div>
          <div className="card" style={{ marginTop: 12 }}>
            <div className="between">
              <span className="kicker">Expedition · {longPath.expedition}</span>
              <span className="tiny" style={{ fontWeight: 800 }}>
                {longPath.position}/{longPath.length}
              </span>
            </div>
            <div
              className="bar"
              role="progressbar"
              aria-label={`${longPath.expedition}: lesson ${String(longPath.position)} of ${String(longPath.length)}`}
              aria-valuemin={0}
              aria-valuemax={longPath.length}
              aria-valuenow={longPath.position - 1}
              style={{ marginTop: 10 }}
            >
              <i
                style={{
                  width: `${String(((longPath.position - 1) / longPath.length) * 100)}%`,
                }}
              />
            </div>
            {/*
              Always a next visible thing: how far to the milestone chest
              (D-151). Optional, so a server a deploy behind shows nothing
              rather than "undefined lessons".
            */}
            {longPath.toMilestone !== undefined && (
              <p className="tiny" style={{ marginTop: 8 }}>
                <span aria-hidden="true">🥈</span>{' '}
                {longPath.toMilestone === 1
                  ? 'This one is a milestone: finish it for a silver chest!'
                  : `${String(longPath.toMilestone)} lessons to the next milestone and its silver chest.`}
              </p>
            )}
          </div>
        </>
      )}

      {/*
        "Play it again, harder." Offered only once the chapter is finished, and
        never as something a child has to do: the next chapter is already open
        ([D-109](../../../docs/DECISIONS.md#d-109)).
      */}
      {longPath === null && replaying === null && replay.kind === 'replay' && (
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 10, minHeight: 48 }}
          onClick={() => {
            if (replay.kind !== 'replay') return;
            setReplaying({
              level: replay.rules.level,
              name: replay.rules.nameL1,
            });
          }}
        >
          🔁 Play it again — {replay.rules.nameL1.toLowerCase()}
        </button>
      )}

      {replaying !== null && (
        <div
          className="card"
          role="status"
          style={{ marginTop: 10, background: '#EAF4FF' }}
        >
          <div style={{ fontWeight: 800 }}>Playing again: {replaying.name}</div>
          <p className="tiny" style={{ marginTop: 4 }}>
            Pick any lesson. You can stop whenever you like.
          </p>
          <button
            type="button"
            className="btn ghost"
            style={{ marginTop: 8, minHeight: 48 }}
            onClick={() => {
              setReplaying(null);
            }}
          >
            Play the normal way
          </button>
        </div>
      )}

      {replay.kind === 'mastered' && (
        <p className="tiny" style={{ marginTop: 8 }}>
          You have taken this chapter all the way. ●
        </p>
      )}

      {longPath === null && (
        <>
          <div className="between" style={{ marginTop: 14 }}>
            <span className="kicker">YOUR WAY THROUGH THE CHAPTER</span>
            <span className="tiny" style={{ fontWeight: 800 }}>
              {finished}/{path.lessons.length}
            </span>
          </div>
          <div className="row" style={{ gap: 8, marginTop: 8 }} aria-hidden="true">
            {path.lessons.map((lesson) => (
              <div
                key={lesson.lessonId}
                className={`lessondot${lesson.state === 'done' ? ' d' : lesson.state === 'now' ? ' n' : ''}`}
              />
            ))}
          </div>
        </>
      )}

      {/*
        A round offered today and never played — the app was closed between
        "yes please" and the first question. Without this it would be earned,
        recorded, and unreachable until tomorrow
        ([D-110](../../../docs/DECISIONS.md#d-110)).
      */}
      {waiting !== null && onBonus !== undefined && (
        <button
          type="button"
          className="btn ghost"
          style={{ marginTop: 12, minHeight: 48 }}
          onClick={() => {
            onBonus(today());
          }}
        >
          {waiting.badge} {waiting.nameL1} is waiting for you
        </button>
      )}

      {/*
        "You found a mistake — it's fixed now. Thank you!"
        ([D-073](../../../docs/DECISIONS.md#d-073)). Above everything else on
        the screen, because it is the rarest thing on it and the only one that
        is about something the *child* did for the app rather than the other
        way round.

        One card however many reports were answered: a list of exercise ids is
        not a thank-you, and a child does not remember which one they tapped.
      */}
      {/*
        First, before any lesson: the first lesson back settles the streak, so
        this is the only moment the question can still help (D-154).
      */}
      <WereYouAway client={client} />

      {fixes.length > 0 && (
        <div
          className="card"
          role="status"
          style={{ marginTop: 14, background: '#FDF6E0' }}
        >
          <div style={{ fontWeight: 800 }}>
            {fixes.length === 1
              ? 'You found a mistake!'
              : `You found ${String(fixes.length)} mistakes!`}
          </div>
          <p className="tiny" style={{ marginTop: 4 }}>
            Somebody looked at it. Thank you for telling us — it really helps.
          </p>
          <button
            className="btn"
            type="button"
            style={{ marginTop: 10 }}
            onClick={thanked}
          >
            You’re welcome
          </button>
        </div>
      )}

      {/*
        Offered above the lessons, because it is what Sneakers would say first
        when there is something to practise — and never framed as a record of
        mistakes. "Let's practise these", never "your mistakes"
        ([Review](../../../docs/REVIEW.md#mistakes-review)).
      */}
      {pool !== null && pool.waiting >= pool.offerAt && (
        <button
          type="button"
          className="opt"
          style={{
            width: '100%',
            textAlign: 'left',
            marginTop: 16,
            borderColor: 'var(--gold)',
            boxShadow: '0 3px 0 #E0BC55',
          }}
          onClick={onPractise}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.875rem' }}>Let’s practise these</div>
            <div className="tiny" style={{ fontWeight: 600 }}>
              {pool.waiting} tricky {pool.waiting === 1 ? 'one' : 'ones'} to beat
            </div>
          </div>
          <span aria-hidden="true">💪</span>
        </button>
      )}

      <div style={{ marginTop: 16 }}>
        {longPath === null && path.lessons.length === 0 && (
          <p className="sub">No lessons have been written for this chapter yet.</p>
        )}
        <Winding
          lessons={
            longPath === null
              ? path.lessons.map((lesson) => ({
                  lessonId: lesson.lessonId,
                  titleL1: lesson.titleL1,
                  state: lesson.state,
                  length: minutes(lesson.estSeconds),
                }))
              : longPath.lessons.map((lesson) => ({
                  lessonId: lesson.lessonId,
                  // A milestone every ten lessons, so there is always a next
                  // visible thing (GENERATION.md).
                  titleL1: lesson.milestone
                    ? `Milestone! ${lesson.titleL1}`
                    : lesson.titleL1,
                  state: lesson.state,
                  length: minutes(lesson.estSeconds),
                }))
          }
          standing={standing}
          companion={companion}
          onPlay={play}
        />
      </div>
    </div>
  );
}
